import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, EnvelopeSimple, Eye, EyeSlash, IdentificationCard, LockKey, Spinner } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

export default function CoachRegister() {
  const { coach, registerCoach } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  if (coach && coach !== null) {
    return <Navigate to="/coach/dashboard" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await registerCoach({ email: email.trim(), password, name: name.trim() });
      toast.success("Account created. Waiting for an admin to approve you.");
      nav("/coach/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Could not register"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 sm:px-8 py-16 lg:py-24">
      <div className="anim-in delay-1">
        <div className="label-kicker" style={{ color: "#1E3A2B" }}>Coach Console</div>
        <h1 className="font-serif text-4xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
          Apply to coach.
        </h1>
        <p className="text-[#4A524C] mt-3 leading-relaxed">
          Create your coach account. An admin will review and approve before you can
          start receiving leader assignments.
        </p>
      </div>
      <form onSubmit={submit} className="mt-8 space-y-4 anim-in delay-2">
        <label className="block">
          <span className="label-kicker block mb-2">Full name</span>
          <div className="relative">
            <IdentificationCard size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="coach-register-name"
              autoFocus
              required
              className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-4 py-3.5 text-[#121815] placeholder-[#8A948E] transition-all"
              placeholder="Jane Coach"
            />
          </div>
        </label>
        <label className="block">
          <span className="label-kicker block mb-2">Email</span>
          <div className="relative">
            <EnvelopeSimple size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="coach-register-email"
              required
              autoComplete="email"
              className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-4 py-3.5 text-[#121815] placeholder-[#8A948E] transition-all"
              placeholder="you@firm.com"
            />
          </div>
        </label>
        <label className="block">
          <span className="label-kicker block mb-2">Password</span>
          <div className="relative">
            <LockKey size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948E]" />
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="coach-register-password"
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:ring-2 focus:ring-[#768C7E]/30 focus:outline-none rounded-xl pl-11 pr-12 py-3.5 text-[#121815] placeholder-[#8A948E] transition-all"
              placeholder="At least 8 characters"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              data-testid="coach-register-toggle-pw"
              aria-label={showPw ? "Hide password" : "Show password"}
              tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md text-[#8A948E] hover:text-[#1E3A2B] hover:bg-[#F3F0EA] transition-colors"
            >
              {showPw ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
            </button>
          </div>
        </label>
        <button
          type="submit"
          disabled={busy}
          data-testid="coach-register-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1E3A2B] text-white px-5 py-3.5 rounded-xl font-medium hover:bg-[#2C4C3B] disabled:opacity-60 transition-colors"
        >
          {busy ? <Spinner size={18} className="animate-spin" /> : <CheckCircle size={18} weight="duotone" />}
          Create coach account
        </button>
        <p className="text-xs text-[#8A948E] text-center pt-2">
          Already have an account? <Link to="/coach/login" className="text-[#1E3A2B] underline">Sign in →</Link>
        </p>
      </form>
    </div>
  );
}
